/**
 * Tipos da frota do credenciado, no Portal do Credenciado.
 *
 * A fonte é `GET /bff/v1/j05-s01-cadastrar-veiculo/state` (ver
 * `comum/recursos.ts`). A tela segue o protótipo (`PortalVeiculos.tsx`,
 * `PortalVeiculoDetail.tsx`), e os campos que ele mostra e a API ainda não
 * devolve estão aqui como `null`: a tela diz "—" neles em vez de inventar.
 * Quando a API passar a devolvê-los, só `paraOVeiculo` (em `dados.ts`) muda.
 */

import type { ItemDoChecklist } from '@/modulos/portal-credenciado/comum/contrato';
import type { EstadoDoCadastro } from '@/modulos/portal-credenciado/comum/recursos';

export const TIPOS_DE_COMBUSTIVEL = [
  'diesel',
  'gasolina',
  'etanol',
  'flex',
  'gnv',
  'eletrico',
] as const;
export type TipoDeCombustivel = (typeof TIPOS_DE_COMBUSTIVEL)[number];

export const ROTULO_DO_TIPO_DE_COMBUSTIVEL: Record<TipoDeCombustivel, string> = {
  diesel: 'Diesel',
  gasolina: 'Gasolina',
  etanol: 'Etanol',
  flex: 'Flex',
  gnv: 'GNV',
  eletrico: 'Elétrico',
};

export interface Veiculo {
  id: string;
  /** Sem pontuação, em maiúsculas. A máscara é da tela. */
  placa: string;
  /** Marca e modelo numa linha só: a API devolve os dois juntos. */
  marca_modelo: string | null;
  /** Estado do cadastro do veículo (análise do DTP). */
  situacao: EstadoDoCadastro;
  documentos: ItemDoChecklist[];

  // Os campos abaixo o protótipo mostra e a API ainda não devolve.
  /** Licença municipal do veículo (CRM), quando emitida. */
  crm: string | null;
  cor: string | null;
  ano_fabricacao: number | null;
  ano_modelo: number | null;
  chassi: string | null;
  renavam: string | null;
  tipo_combustivel: TipoDeCombustivel | null;
  /** Veículo com adaptação para pessoa com deficiência (PCD). `null` quando não se sabe. */
  acessivel: boolean | null;
  capacidade_detran: number | null;
  capacidade_teg: number | null;
  capacidade_total: number | null;
  capacidade_total_origem: 'SGTP' | 'MANUAL' | null;
  capacidade_sgtp_snapshot: number | null;
  ultima_consulta_sgtp_capacidade: string | null;
  capacidade_creche: number | null;
  capacidade_ancoragens_pcd: number | null;
  valor_mensal: number | null;
  crlv_validade: string | null;
  seguro_seguradora: string | null;
  seguro_apolice: string | null;
  seguro_validade: string | null;
  seguro_app_valor: number | null;
  seguro_app_vencimento: string | null;
  seguro_rc_valor: number | null;
  seguro_rc_vencimento: string | null;
  vistoria_validade: string | null;
  observacoes: string | null;
}

/** Os filtros da listagem: busca e situação, com as chaves do protótipo na URL. */
export interface FiltroDeVeiculos {
  busca?: string;
  situacao?: EstadoDoCadastro;
}

/**
 * Veículo declarado acessível sem ancoragem validada — não pode ser vinculado
 * a uma OS acessível até a vistoria interna do DTP.
 */
export function aguardaValidacaoDeAncoragens(veiculo: Veiculo): boolean {
  return veiculo.acessivel === true && veiculo.capacidade_ancoragens_pcd === 0;
}
